import { css } from 'styled-components';
import { EORA_EFFECTS } from '../effects/eoraEffects';

export const SHADOW_TYPES = {
  LIGHT: 'light',
  DEFAULT: 'default',
  BOLD: 'bold',
  STRONG: 'strong',
  XSTRONG: 'xstrong',
  INVERTED: 'inverted'
};

type ShadowTypes = typeof SHADOW_TYPES;
export type ShadowType = ShadowTypes[keyof ShadowTypes];

/**
 * @deprecated Prefer SHADOW_TYPE
 */
export const SHADOW_SIZES = {
  E100: 'e100',
  E200: 'e200',
  E300: 'e300',
  E400: 'e400',
  E500: 'e500'
} as const;

/**
 * @deprecated Prefer ShadowType
 */
type ShadowSizes = typeof SHADOW_SIZES;
/**
 * @deprecated Prefer ShadowType
 */
export type ShadowSize = ShadowSizes[keyof ShadowSizes];

export interface IShadowProps {
  shadow?: boolean | ShadowSize | ShadowType;
  shadowAsFilter?: boolean;
}

export const generateShadows = () => {
  const shadows = EORA_EFFECTS.EFFECTS.STYLE.SHADOW as any;
  const getShadow = (shadowType: ShadowType): string => shadows[`SHADOW_${shadowType.toUpperCase()}`];

  const boxShadow = (shadowType: ShadowType) => css`
    box-shadow: ${getShadow(shadowType)};
  `;

  const dropShadow = (shadowType: ShadowType) => {
    // Bit of a hack. Values stored in EORA_EFFECTS.EFFECTS.STYLE.SHADOW are comma separated
    // lists as string in the form of 0px 0px 0px 0px rgba(R, G, B, A), 0px 0px 0px 0px rgba(R, G, B, A)
    // this code splits around commas and rejoins individual shadows as drop-shadow(MY_SHADOW)
    const parts = getShadow(shadowType).split(',');
    return css`
      filter: ${Array.from({ length: parts.length / 4 })
        .map((_, index) => index)
        .map(
          (index) =>
            `drop-shadow(${parts
              .slice(index * 4, index * 4 + 4)
              .join('')
              .trim()})`
        )
        .join(' ')};
    `;
  };

  return {
    boxShadow: {
      [SHADOW_SIZES.E100]: boxShadow(SHADOW_TYPES.LIGHT),
      [SHADOW_SIZES.E200]: boxShadow(SHADOW_TYPES.DEFAULT),
      [SHADOW_SIZES.E300]: boxShadow(SHADOW_TYPES.BOLD),
      [SHADOW_SIZES.E400]: boxShadow(SHADOW_TYPES.STRONG),
      [SHADOW_SIZES.E500]: boxShadow(SHADOW_TYPES.XSTRONG),
      [SHADOW_TYPES.LIGHT]: boxShadow(SHADOW_TYPES.LIGHT),
      [SHADOW_TYPES.DEFAULT]: boxShadow(SHADOW_TYPES.DEFAULT),
      [SHADOW_TYPES.BOLD]: boxShadow(SHADOW_TYPES.BOLD),
      [SHADOW_TYPES.STRONG]: boxShadow(SHADOW_TYPES.STRONG),
      [SHADOW_TYPES.XSTRONG]: boxShadow(SHADOW_TYPES.XSTRONG),
      [SHADOW_TYPES.INVERTED]: boxShadow(SHADOW_TYPES.INVERTED)
    },
    dropShadow: {
      [SHADOW_SIZES.E100]: dropShadow(SHADOW_TYPES.LIGHT),
      [SHADOW_SIZES.E200]: dropShadow(SHADOW_TYPES.DEFAULT),
      [SHADOW_SIZES.E300]: dropShadow(SHADOW_TYPES.BOLD),
      [SHADOW_SIZES.E400]: dropShadow(SHADOW_TYPES.STRONG),
      [SHADOW_SIZES.E500]: dropShadow(SHADOW_TYPES.XSTRONG),
      [SHADOW_TYPES.LIGHT]: dropShadow(SHADOW_TYPES.LIGHT),
      [SHADOW_TYPES.DEFAULT]: dropShadow(SHADOW_TYPES.DEFAULT),
      [SHADOW_TYPES.BOLD]: dropShadow(SHADOW_TYPES.BOLD),
      [SHADOW_TYPES.STRONG]: dropShadow(SHADOW_TYPES.STRONG),
      [SHADOW_TYPES.XSTRONG]: dropShadow(SHADOW_TYPES.XSTRONG),
      [SHADOW_TYPES.INVERTED]: dropShadow(SHADOW_TYPES.INVERTED)
    }
  };
};

// shadowAsFilter uses "filter: drop-shadow()"" instead of "box-shadow"
// box-shadow is more efficient and suffices for most elements
// however irregular shapes (i.e. non-box) require drop-shadow
export const useShadowCss = (defaultShadowProps?: IShadowProps) => css<IShadowProps>`
  ${({ shadow = defaultShadowProps?.shadow, shadowAsFilter = defaultShadowProps?.shadowAsFilter, theme }) =>
    shadow &&
    (shadowAsFilter
      ? theme.shadows.dropShadow[shadow === true ? SHADOW_TYPES.DEFAULT : shadow]
      : theme.shadows.boxShadow[shadow === true ? SHADOW_TYPES.DEFAULT : shadow])};
  ${({ shadow, shadowAsFilter }) =>
    shadow === false &&
    (shadowAsFilter
      ? css`
          filter: drop-shadow();
        `
      : css`
          box-shadow: none;
        `)};
`;
